import React, { useState } from 'react';
import { Calendar, Clock, Droplets, Zap, Play, Plus, Pencil, Trash2, Power, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import {
  useIrrigation,
  IrrigationProgram,
  programScheduleLabel,
  totalDailyMinutes,
  formatDuration,
} from '../contexts/IrrigationContext';
import ProgramModal from '../components/ProgramModal';

const Schedules: React.FC = () => {
  const { programs, runtime, now, zoneById, deleteProgram, toggleProgram, runProgramNow } = useIrrigation();
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IrrigationProgram | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (p: IrrigationProgram) => {
    setEditing(p);
    setModalOpen(true);
  };

  const handleRun = (p: IrrigationProgram) => {
    runProgramNow(p.id);
    toast.success(`Lancement de « ${p.name} » en cours…`);
  };

  const handleDelete = (p: IrrigationProgram) => {
    deleteProgram(p.id);
    toast.success(`Programme « ${p.name} » supprimé.`);
  };

  const handleToggle = (p: IrrigationProgram) => {
    toggleProgram(p.id);
    toast.success(p.enabled ? `« ${p.name} » désactivé.` : `« ${p.name} » activé.`);
  };

  const filtered = programs.filter((p) => {
    const typeMatch = filterType === 'all' || p.type === filterType;
    const statusMatch =
      filterStatus === 'all' ||
      (filterStatus === 'enabled' && p.enabled) ||
      (filterStatus === 'disabled' && !p.enabled);
    return typeMatch && statusMatch;
  });

  const stats = [
    { label: 'Total programmes', value: programs.length, color: 'var(--color-primary)', icon: Calendar },
    { label: 'Actifs', value: programs.filter((p) => p.enabled).length, color: 'var(--color-success)', icon: Play },
    { label: 'Irrigation', value: programs.filter((p) => p.type === 'irrigation').length, color: 'var(--color-info)', icon: Droplets },
    { label: 'Fertigation', value: programs.filter((p) => p.type === 'fertigation').length, color: 'var(--color-accent)', icon: Zap },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">Plans d&apos;irrigation</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Créez et gérez vos programmes hebdomadaires ou cycliques avec plusieurs plages horaires
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
          data-testid="new-schedule-button"
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Nouveau programme</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="card text-center" data-testid={`schedule-stat-${i}`}>
            <s.icon className="w-6 h-6 mx-auto mb-2" style={{ color: s.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>
              {s.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-[var(--text-primary)]">Type :</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
            data-testid="schedule-type-filter"
          >
            <option value="all">Tous les types</option>
            <option value="irrigation">Irrigation</option>
            <option value="fertigation">Fertigation</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-[var(--text-primary)]">Statut :</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
            data-testid="schedule-status-filter"
          >
            <option value="all">Tous</option>
            <option value="enabled">Actifs</option>
            <option value="disabled">Désactivés</option>
          </select>
        </div>
      </div>

      {/* Programs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filtered.map((p) => {
          const zone = zoneById(p.zone_id);
          const TypeIcon = p.type === 'fertigation' ? Zap : Droplets;
          const color = p.type === 'fertigation' ? 'var(--color-accent)' : 'var(--color-primary)';
          const dailyMin = totalDailyMinutes(p.time_ranges);
          const session = runtime[p.zone_id]?.session;
          const running = session && session.program_id === p.id;
          return (
            <div
              key={p.id}
              className={`card transition-all hover:shadow-lg ${!p.enabled ? 'opacity-60' : ''}`}
              data-testid={`schedule-card-${p.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
                    <TypeIcon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div>
                    <h3 className="text-heading-4 text-[var(--text-primary)]">{p.name}</h3>
                    <p className="text-body-sm text-[var(--text-secondary)]">
                      {zone ? `${zone.name} — ${zone.field_name}` : p.zone_id}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)] capitalize">{p.type}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {p.enabled ? (
                    <span className="px-2 py-1 text-xs bg-[var(--color-success)] text-white rounded-full">Actif</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-[var(--color-danger)] text-white rounded-full">Désactivé</span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-[var(--gray-50)] rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    {p.mode === 'weekly' ? (
                      <Calendar className="w-4 h-4 text-[var(--text-secondary)]" />
                    ) : (
                      <Repeat className="w-4 h-4 text-[var(--text-secondary)]" />
                    )}
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {p.mode === 'weekly' ? 'Hebdomadaire' : 'Cyclique'}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">{programScheduleLabel(p)}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {p.time_ranges.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[var(--surface)] border border-[var(--border-light)] rounded-md text-[var(--text-secondary)]"
                      >
                        <Clock className="w-3 h-3" />
                        {r.start}–{r.end}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)] mt-2">Durée totale/jour : {dailyMin} min</p>
                </div>

                {running && session.ends_at && (
                  <div className="p-3 rounded-lg bg-[var(--color-success)]/10 border border-[var(--color-success)]/30">
                    <p className="text-sm font-medium text-[var(--color-success)]">
                      Irrigation en cours — fin dans {formatDuration(new Date(session.ends_at).getTime() - now)}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[var(--border-light)]">
                <button
                  onClick={() => handleRun(p)}
                  className="flex-1 min-w-[120px] flex items-center justify-center space-x-2 px-3 py-2 bg-[var(--color-success)] text-white rounded-lg hover:opacity-90"
                  data-testid={`run-schedule-${p.id}`}
                >
                  <Play className="w-4 h-4" />
                  <span className="text-sm">Lancer maintenant</span>
                </button>
                <button
                  onClick={() => handleToggle(p)}
                  className="px-3 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--gray-50)]"
                  title={p.enabled ? 'Désactiver' : 'Activer'}
                  data-testid={`toggle-schedule-${p.id}`}
                >
                  <Power className={`w-4 h-4 ${p.enabled ? 'text-[var(--color-success)]' : 'text-[var(--text-secondary)]'}`} />
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="px-3 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--gray-50)]"
                  title="Modifier"
                  data-testid={`edit-schedule-${p.id}`}
                >
                  <Pencil className="w-4 h-4 text-[var(--text-secondary)]" />
                </button>
                <button
                  onClick={() => handleDelete(p)}
                  className="px-3 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--color-danger)] hover:text-white hover:border-[var(--color-danger)]"
                  title="Supprimer"
                  data-testid={`delete-schedule-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12" data-testid="schedules-empty-state">
          <Calendar className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">Aucun programme trouvé</h3>
          <p className="text-body text-[var(--text-secondary)]">
            Ajustez les filtres ou créez un nouveau programme d&apos;irrigation
          </p>
        </div>
      )}

      {modalOpen && <ProgramModal editing={editing} onClose={() => setModalOpen(false)} />}
    </div>
  );
};

export default Schedules;
